import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { initialProjetosListaFiltersState, useProjetosListaFilters } from "../hooks/Projetos_lista/filter";
import { useProjetosListaPagination } from "../hooks/Projetos_lista/pagination";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { ClienteDropDown } from "../../../../common/domain/models/cliente_dropdown";
import { EmailForm } from "../../../../common/presentation/schemas/email";
import { emailTemplates } from "../../../../common/utils/email_templates";
import { ProjetoLista } from "../../domain/models/projetos_Lista";
import { useNavigate } from "react-router";



type LoadProjetoFuncState = {
  nomeProjeto?: string | null;
  contato?: string | null;
  idCliente?: number[] | null;
  idCentroCusto?: number[] | null;
  page?: number | null;
  rowsPerPage?: number | null;
};


export const useProjetoListaViewModel = () => {
  const {
    commons: {
      getClientesDropDownUseCase,
      getCentroCustoDropDownUseCase,
    },
    projetos: {
      getAllProjetoListaUseCase,
      deleteProjetoUseCase,
      sendEmailProjetoUseCase,
    },
  } = useDependencyInjection();


  const { pushInfoNotification, pushErrorNotification, setPageSaving } = usePageLoader();

  const navigate = useNavigate();

  const debouncedLoad = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [emailSendTo, setEmailSendTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailAttachment, setEmailAttachment] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const [projetoLista, setProjetoLista] = useState<ProjetoLista[]>();
  const [selectedProjeto, setSelectedProjeto] = useState<ProjetoLista>();

  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>();
  const [clienteDropdown, setClienteDropdown] = useState<ClienteDropDown[]>();

  const [projetoDeleteModal, setProjetoDeleteModal] = useState(false);

  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [openLegendaModal, setOpenLegendaModal] = useState(false);

  const filters = useProjetosListaFilters();
  const pagination = useProjetosListaPagination();


  //#region  LOAD
  const loadProjetos = useCallback(async (funcState?: LoadProjetoFuncState) => {
    try {
      setLoading(true);
      const filterNomeProjeto = evaluateParamValue(funcState?.nomeProjeto, filters.nomeProjeto);
      const filterIdCliente = evaluateParamValue(funcState?.idCliente, filters.idCliente);
      const filterContato = evaluateParamValue(funcState?.contato, filters.contato);
      const filterIdCentroCusto = evaluateParamValue(funcState?.idCentroCusto, filters.idCentroCusto);

      const filterPage = funcState?.page ?? pagination.page;
      const filterRowspage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllProjetoListaUseCase.getAllProjetoLista(
        { contato: filterContato, idCentroCusto: filterIdCentroCusto, idCliente: filterIdCliente, nomeProjeto: filterNomeProjeto },
        filterPage,
        filterRowspage,
      );

      setProjetoLista(getAllProjetoListaUseCase.items);
      pagination.setTotalItems(getAllProjetoListaUseCase.totalItems);
      pagination.setRowsPerPage(getAllProjetoListaUseCase.pageSize);
      setLoading(false);
    } catch (e: unknown) {
      setLoading(false);
      setProjetoLista([]);
      throw new Error("Falha ao buscar projetos!");
    }
  }, [filters.contato, filters.idCentroCusto, filters.idCliente, filters.nomeProjeto, getAllProjetoListaUseCase, pagination]);

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      const centroCusto = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      setCentroCustoDropdown(centroCusto);
    } catch (e: unknown) {
      setCentroCustoDropdown(undefined);
      throw new Error('Falha ao buscar centros de custo!');
    }
  }, [getCentroCustoDropDownUseCase]);

  const loadClienteDropDown = useCallback(async () => {
    try {
      const cliente = await getClientesDropDownUseCase.getClientesDropDown();
      setClienteDropdown(cliente);
    } catch (e: unknown) {
      setClienteDropdown(undefined);
      throw new Error('Falha ao buscar clientes!');
    }
  }, [getClientesDropDownUseCase]);

  //#endregion


  //#region  useEffect

  usePageStartBootstrap(async () => {
    try {
      await loadProjetos();
      await loadCentroCustoDropDown();
      await loadClienteDropDown();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das paginas!")[0]);
    }
  });
  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadProjetos();
      await loadCentroCustoDropDown();
      await loadClienteDropDown();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadCentroCustoDropDown, loadClienteDropDown, loadProjetos, pushErrorNotification]);

  //#endregion

  //#region  Eventos


  const handleProjetoDeleteOpenState = useCallback((idProjeto: number | null, openState: boolean) => {
    if (openState) {
      if (idProjeto && projetoLista) {
        setSelectedProjeto(projetoLista.find((p) => p.IdProjeto === idProjeto));
      } else {
        setSelectedProjeto(undefined);
      }
    }
    setProjetoDeleteModal(openState);
  }, [
    projetoLista,
  ]);

  const handleProjetoDeleteSubmit = useCallback(async () => {
    if (selectedProjeto) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteProjetoUseCase.deleteProjeto(selectedProjeto.IdProjeto);

        await loadProjetos();

        setSelectedProjeto(undefined);
        setProjetoDeleteModal(false);

        pushInfoNotification("Sucesso", "Projeto excluído com sucesso!");

      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao excluir projeto!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [selectedProjeto, setPageSaving, deleteProjetoUseCase, loadProjetos, pushInfoNotification, pushErrorNotification]);

  const handleProjetoEmailOpenState = useCallback(async (idProjeto: number | null, openState: boolean) => {
    if (openState) {
      if (idProjeto && projetoLista) {
        setEmailSendTo("");
        setEmailSubject("Plano de Projeto - " + idProjeto);
        setEmailAttachment(`PLANO_PROJETO_${idProjeto}.pdf`);
        setEmailBody(emailTemplates.projeto);

        setSelectedProjeto(projetoLista.find((p) => p.IdProjeto === idProjeto));
      } else {
        setSelectedProjeto(undefined);
      }
    }
    setOpenEmailModal(openState);
  }, [
    projetoLista,
  ]);

  const handleEmailSubmit = useCallback(async (form: EmailForm) => {
    if (selectedProjeto) {
      try {
        setLoading(true);

        await sendEmailProjetoUseCase.sendEmailProjeto(
          selectedProjeto.IdProjeto,
          form.sendTo,
          form.subject,
          form.body,
          emailAttachment,
        );

        pushInfoNotification("Sucesso", "O email enviado com sucesso!");

      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao enviar email!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    selectedProjeto,
    emailAttachment,
    sendEmailProjetoUseCase,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useProjetosListaPagination.setState({ page: page + 1 });
      await loadProjetos({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadProjetos,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useProjetosListaPagination.setState({ page: 1, rowsPerPage });
      await loadProjetos({ page: 1, rowsPerPage: rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadProjetos,
  ]);

  const handleCentroCustoChange = useCallback(async (idCentroCusto: number[]) => {
    try {
      setLoading(true);
      filters.setIdCentroCusto(idCentroCusto)
      pagination.setPage(1);
      await loadProjetos({ page: 1, idCentroCusto });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [filters, loadProjetos, pagination, pushErrorNotification]);

  const handleClienteChange = useCallback(async (idCliente: number[]) => {
    try {
      setLoading(true);
      filters.setIdCliente(idCliente);
      pagination.setPage(1);
      await loadProjetos({ page: 1, idCliente });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [filters, loadProjetos, pagination, pushErrorNotification]);

  const handleNomeProjetoChange = useDebouncedCallback(debouncedLoad, (nomeProjeto: string) => {
    useProjetosListaFilters.setState({ nomeProjeto });
    return async () => {
      try {
        setLoading(true);
        useProjetosListaPagination.setState({ page: 1 });
        await loadProjetos({ page: 1, nomeProjeto });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    };
  }, [loadProjetos, pushErrorNotification]);

  const handleContatoChange = useDebouncedCallback(debouncedLoad, (contato: string) => {
    useProjetosListaFilters.setState({ contato });
    return async () => {
      try {
        setLoading(true);
        useProjetosListaPagination.setState({ page: 1 });
        await loadProjetos({ page: 1, contato });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    };
  }, [filters, loadProjetos]);

  const handleClear = useCallback(async () => {
    try {
      setLoading(true);
      useProjetosListaFilters.setState({ ...initialProjetosListaFiltersState });
      useProjetosListaPagination.setState({ page: 1 });
      await loadProjetos({ ...initialProjetosListaFiltersState, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [loadProjetos, pushErrorNotification]);


  const handleProjeto = useCallback((id: number | null) => {
    if (id) navigate(`/Projetos_cadastro/${id}`,);
    else navigate(`/Projetos_cadastro`);

  }, [navigate]);

  const handleShowLegenda = useCallback((openState: boolean) => {
    setOpenLegendaModal(openState);
  }, []);

  //#endregion

  return {
    loading,
    saving,
    errorMessage,
    emailSendTo,
    emailSubject,
    emailAttachment,
    emailBody,
    projetoLista: projetoLista ?? [],
    selectedProjeto: selectedProjeto ?? null,
    centroCustoDropdown: centroCustoDropdown ?? [],
    clienteDropdown: clienteDropdown ?? [],
    projetoDeleteModal,
    openEmailModal,
    filters,
    pagination,
    openLegendaModal,
    handleShowLegenda,
    handleReload,
    handleProjetoDeleteOpenState,
    handleProjetoDeleteSubmit,
    handleProjetoEmailOpenState,
    handleEmailSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleClear,
    handleNomeProjetoChange,
    handleContatoChange,
    handleCentroCustoChange,
    handleClienteChange,
    handleProjeto
  };
};
